import * as React from "react";
import { LabelValue } from "components/LabelValue/LabelValue";
import {
  ClientConfig,
  ClientDataType,
  ClientsService,
  ForbiddenFileType,
  LanguagesService,
  PdfConversionTargetFormat,
  ProjectSubmissionMode
} from "gen/clients";
import { useMutation, useQuery } from "react-query";
import { useDialogState } from "hooks/useDialogState";
import { Formik, Form } from "formik";
import { LoadingButton } from "components/LoadingButton/LoadingButton";
import { useClientReadOnly } from "contexts/ClientLockContext";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import DialogContentText from "@mui/material/DialogContentText";
import Stack from "@mui/material/Stack";
import FormHelperText from "@mui/material/FormHelperText";
import Link from "@mui/material/Link";
import { TextInputField } from "components/formikFields/TextInputField/TextInputField";
import { ConfirmationDialog } from "components/ConfirmationDialog/ConfirmationDialog";
import { SnackbarApiError } from "components/SnackbarApiError/SnackbarApiError";
import { CopyToClipboardButton } from "components/CopyToClipboardButton/CopyToClipboardButton";
import { SelectOneField } from "components/formikFields/SelectOneField/SelectOneField";
import { UploadImageToClientConfigDialog } from "../../../../components/UploadImageToClientConfigDialog";
import { SelectOption } from "@mui/base";
import Grid from "@mui/material/Grid";
import { SelectOneAutocompleteField } from "components/formikFields/SelectOneAutocompleteField/SelectOneAutocompleteField";
import { CheckboxField } from "components/formikFields/CheckboxField/CheckboxField";
import { SelectManyAutocompleteField } from "components/formikFields/SelectManyAutocompleteField/SelectManyAutocompleteField";
import { SELECT_OPTION_COMPARATOR } from "utils/stringUtils";
import { Typography } from "@mui/material";
import { SubmissionModeConditions } from "./components/SubmissionModeConditions";
import { ScrollToFieldError } from "../../../../../../../components/formikFields/ScrollToFieldError/ScrollToFieldError";

interface Props {
  clientConfig: ClientConfig;
  onClose: () => void;
  onSaveSuccess: () => void;
}

export enum FieldName {
  INSTRUCTIONAL_TEXT = "instructionalText",
  FOOTER_MESSAGE = "footerMessage",
  PROJECT_SUBMISSION_MODE = "projectSubmissionMode",
  ENABLE_PDF_CONVERSION = "pdfConversionIsEnabled",
  PDF_CONVERSION_TARGET_FORMAT = "pdfConversionTargetFormat",
  ENABLE_IMAGE_CONVERSION = "imageConversionIsEnabled",
  DEFAULT_SOURCE_LANGUAGE = "defaultSourceLanguage",
  IS_SOURCE_LANGUAGE_HIDDEN = "isSourceLanguageHidden",
  IS_SOURCE_LANGUAGE_DISABLED = "isSourceLanguageDisabled",
  DISABLE_SOURCE_SHOW_ALL_LANGUAGES = "disableSourceShowAllLanguages",
  DEFAULT_TARGET_LANGUAGES = "defaultTargetLanguages",
  IS_TARGET_LANGUAGE_HIDDEN = "isTargetLanguageHidden",
  IS_TARGET_LANGUAGE_DISABLED = "isTargetLanguageDisabled",
  DISABLE_TARGET_SHOW_ALL_LANGUAGES = "disableTargetShowAllLanguages",
  SUBMISSION_MODE_CONDITION_FIELD_NAME = "submissionModeConditionFieldName",
  SUBMISSION_MODE_CONDITION_FIELD_VALUE = "submissionModeConditionFieldValue",
  DISABLE_UNAUTHENTICATED_SUBMISSIONS = "disableUnauthenticatedSubmissions",
  SKIP_EMAIL_DOMAIN_VALIDATION = "skipEmailDomainValidation",
  DISABLE_SOURCE_LANGUAGE_DETECTION = "disableSourceLanguageDetection",
  FORBIDDEN_FILE_TYPES = "forbiddenFileTypes"
}

export interface FormValues {
  [FieldName.INSTRUCTIONAL_TEXT]: string;
  [FieldName.FOOTER_MESSAGE]: string;
  [FieldName.PROJECT_SUBMISSION_MODE]: ProjectSubmissionMode;
  [FieldName.ENABLE_PDF_CONVERSION]: boolean;
  [FieldName.PDF_CONVERSION_TARGET_FORMAT]: PdfConversionTargetFormat | undefined;
  [FieldName.ENABLE_IMAGE_CONVERSION]: boolean;
  [FieldName.DEFAULT_SOURCE_LANGUAGE]: SelectOption<string> | null;
  [FieldName.IS_SOURCE_LANGUAGE_HIDDEN]: boolean;
  [FieldName.IS_SOURCE_LANGUAGE_DISABLED]: boolean;
  [FieldName.DISABLE_SOURCE_SHOW_ALL_LANGUAGES]: boolean;
  [FieldName.DEFAULT_TARGET_LANGUAGES]: SelectOption<string>[];
  [FieldName.IS_TARGET_LANGUAGE_HIDDEN]: boolean;
  [FieldName.IS_TARGET_LANGUAGE_DISABLED]: boolean;
  [FieldName.DISABLE_TARGET_SHOW_ALL_LANGUAGES]: boolean;
  [FieldName.SUBMISSION_MODE_CONDITION_FIELD_NAME]: string;
  [FieldName.SUBMISSION_MODE_CONDITION_FIELD_VALUE]: string;
  [FieldName.DISABLE_UNAUTHENTICATED_SUBMISSIONS]: boolean;
  [FieldName.SKIP_EMAIL_DOMAIN_VALIDATION]: boolean;
  [FieldName.DISABLE_SOURCE_LANGUAGE_DETECTION]: boolean;
  [FieldName.FORBIDDEN_FILE_TYPES]: SelectOption<string>[];
}

const CONTENT_ROOT_URL = import.meta.env.VITE_STATIC_CONTENT_ROOT_URL;

const FORBIDDEN_FILE_TYPE_OPTIONS: SelectOption<string>[] = [
  { label: "Executable (.exe)", value: ForbiddenFileType.EXE },
  { label: "Java Archive (.jar)", value: ForbiddenFileType.JAR }
];

function containsScriptTag(htmlString: string) {
  // This regular expression looks for any <script> tag (case-insensitive)
  const scriptRegex = /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi;
  return scriptRegex.test(htmlString);
}

const CreateProjectSettings: React.FC<Props> = ({ clientConfig, onSaveSuccess, onClose }) => {
  const isReadOnly = useClientReadOnly();
  const [projectCreationToken, setProjectCreationToken] = React.useState<string | undefined>(
    clientConfig.createProjectToken
  );
  const [isBannerUploadDialogOpen, openBannerUploadDialog, closeBannerUploadDialog] = useDialogState();
  const [isDeleteUrlConfirmationDialogOpen, openDeleteUrlConfirmationDialog, closeDeleteUrlConfirmationDialog] =
    useDialogState();

  const { data: lltsLanguages, isLoading: areLltsLanguagesLoading } = useQuery("listLltsLanguages", {
    queryFn: () => LanguagesService.listLltsLanguages({ xtrfIdExists: true })
  });

  const lltsLanguageOptions: SelectOption<string>[] = React.useMemo(
    () => (lltsLanguages || []).map(l => ({ label: l.name, value: l.id })).sort(SELECT_OPTION_COMPARATOR),
    [lltsLanguages]
  );

  const {
    mutate,
    error: mutateError,
    isLoading
  } = useMutation("updateClientConfig", {
    onSuccess: onSaveSuccess,
    mutationFn: ClientsService.updateClientConfig
  });

  const onDeleteBannerClick = React.useCallback(() => {
    const clientConfigUpdate = {
      ...clientConfig,
      bannerKey: undefined
    };
    mutate({ id: clientConfigUpdate.id, requestBody: clientConfigUpdate });
  }, [clientConfig, mutate]);

  const onGenerateProjectCreationUrlClick = React.useCallback(() => {
    let token = "";
    for (let i = 0; i < 8; i++) {
      token += Math.floor(Math.random() * 10);
    }
    setProjectCreationToken(token);
    mutate({
      id: clientConfig.id,
      requestBody: {
        ...clientConfig,
        createProjectToken: token
      }
    });
  }, [setProjectCreationToken, clientConfig, mutate]);

  const onDeleteProjectCreationUrlConfirmation = React.useCallback(() => {
    setProjectCreationToken("");
    mutate(
      {
        id: clientConfig.id,
        requestBody: {
          ...clientConfig,
          createProjectToken: undefined,
          createProjectSettings: {
            ...clientConfig.createProjectSettings,
            skipEmailDomainValidation: false
          }
        }
      },
      {
        onSuccess: () => {
          closeDeleteUrlConfirmationDialog();
          onSaveSuccess();
        }
      }
    );
  }, [mutate, clientConfig, closeDeleteUrlConfirmationDialog, onSaveSuccess]);

  const validate = React.useCallback((formValues: FormValues) => {
    const errors: Record<string, string> = {};
    if (formValues.instructionalText && containsScriptTag(formValues.instructionalText)) {
      errors.instructionalText = "Instructional text cannot contain script tags";
    }
    if (formValues.footerMessage && containsScriptTag(formValues.footerMessage)) {
      errors.footerMessage = "Footer message cannot contain script tags";
    }
    return errors;
  }, []);

  const onSubmit = React.useCallback(
    async (formValues: FormValues) => {
      const defaultSourceLanguage = formValues[FieldName.DEFAULT_SOURCE_LANGUAGE]?.value
        ? lltsLanguages?.find(l => l.id === formValues[FieldName.DEFAULT_SOURCE_LANGUAGE]?.value)
        : undefined;
      const defaultTargetLanguages = formValues[FieldName.DEFAULT_TARGET_LANGUAGES].map(
        selectedOption => (lltsLanguages || []).find(l => l.id === selectedOption.value)!
      );
      const clientConfigUpdate: ClientConfig = {
        ...clientConfig,
        instructionalText: formValues[FieldName.INSTRUCTIONAL_TEXT] || undefined,
        projectSubmissionMode: formValues[FieldName.PROJECT_SUBMISSION_MODE],
        projectSubmissionFooterMessage: formValues[FieldName.FOOTER_MESSAGE] || undefined,
        createProjectSettings: {
          sourceLanguage: {
            defaultLanguage: defaultSourceLanguage,
            isHidden: defaultSourceLanguage ? formValues[FieldName.IS_SOURCE_LANGUAGE_HIDDEN] : false,
            isDisabled: defaultSourceLanguage ? formValues[FieldName.IS_SOURCE_LANGUAGE_DISABLED] : false,
            disableShowAllLanguages: formValues[FieldName.DISABLE_SOURCE_SHOW_ALL_LANGUAGES]
          },
          targetLanguages: {
            defaultLanguages: defaultTargetLanguages,
            isHidden: defaultTargetLanguages.length > 0 ? formValues[FieldName.IS_TARGET_LANGUAGE_HIDDEN] : false,
            isDisabled: defaultTargetLanguages.length > 0 ? formValues[FieldName.IS_TARGET_LANGUAGE_DISABLED] : false,
            disableShowAllLanguages: formValues[FieldName.DISABLE_TARGET_SHOW_ALL_LANGUAGES]
          },
          submissionModeConditions:
            formValues[FieldName.SUBMISSION_MODE_CONDITION_FIELD_NAME] &&
            formValues[FieldName.SUBMISSION_MODE_CONDITION_FIELD_VALUE]
              ? [
                  {
                    fieldName: formValues[FieldName.SUBMISSION_MODE_CONDITION_FIELD_NAME],
                    value: formValues[FieldName.SUBMISSION_MODE_CONDITION_FIELD_VALUE]
                  }
                ]
              : undefined,
          pdfConversion: {
            isDisabled: !formValues[FieldName.ENABLE_PDF_CONVERSION],
            targetFormat: formValues[FieldName.ENABLE_PDF_CONVERSION]
              ? formValues[FieldName.PDF_CONVERSION_TARGET_FORMAT] || PdfConversionTargetFormat.WORD_DOCUMENT
              : undefined
          },
          imageConversion: formValues[FieldName.ENABLE_IMAGE_CONVERSION] ? undefined : { isDisabled: true },
          disableUnauthenticatedSubmissions: formValues[FieldName.DISABLE_UNAUTHENTICATED_SUBMISSIONS],
          skipEmailDomainValidation: clientConfig.createProjectToken
            ? formValues[FieldName.SKIP_EMAIL_DOMAIN_VALIDATION]
            : false,
          disableSourceLanguageDetection: formValues[FieldName.DISABLE_SOURCE_LANGUAGE_DETECTION],
          forbiddenFileTypes: formValues[FieldName.FORBIDDEN_FILE_TYPES].map(
            selectedOption => selectedOption.value as ForbiddenFileType
          )
        }
      };
      await mutate({ id: clientConfigUpdate.id, requestBody: clientConfigUpdate });
    },
    [clientConfig, lltsLanguages, mutate]
  );

  return (
    <>
      <div>
        <Stack spacing={2} sx={{ mb: 2 }}>
          <LabelValue
            label="Banner"
            value={
              <div>
                {!clientConfig.bannerKey && (
                  <>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={openBannerUploadDialog}
                      disabled={isLoading || isReadOnly}
                    >
                      Add
                    </Button>
                    <FormHelperText sx={{ display: "inline", ml: 1 }}>
                      (Recommended image dimensions are 900x200 pixels)
                    </FormHelperText>
                  </>
                )}
                {clientConfig.bannerKey && (
                  <span>
                    <div>
                      <Link href={`${CONTENT_ROOT_URL}/${clientConfig.bannerKey}`} target="_blank">
                        {clientConfig.bannerKey.substring(clientConfig.bannerKey.lastIndexOf("/") + 1)}
                      </Link>
                      <Button
                        onClick={onDeleteBannerClick}
                        size="small"
                        variant="outlined"
                        disabled={isLoading || isReadOnly}
                        sx={{ ml: 1 }}
                      >
                        Delete
                      </Button>
                    </div>
                  </span>
                )}
              </div>
            }
          />
          <LabelValue
            label="Project Creation URL"
            value={
              <>
                <div>
                  {projectCreationToken && (
                    <span>
                      {`${import.meta.env.VITE_PUBLIC_PORTAL_URL}/createProject/${encodeURIComponent(
                        btoa(clientConfig.id)
                      )}/${projectCreationToken}`}
                    </span>
                  )}
                  {!projectCreationToken && (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={onGenerateProjectCreationUrlClick}
                      disabled={isReadOnly}
                    >
                      <>Generate</>
                    </Button>
                  )}
                  {projectCreationToken && (
                    <>
                      <CopyToClipboardButton
                        variant="outlined"
                        size="small"
                        sx={{ ml: 1 }}
                        textToCopy={`${import.meta.env.VITE_PUBLIC_PORTAL_URL}/createProject/${encodeURIComponent(
                          btoa(clientConfig.id)
                        )}/${projectCreationToken}`}
                      >
                        Copy
                      </CopyToClipboardButton>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={openDeleteUrlConfirmationDialog}
                        disabled={isReadOnly}
                        sx={{ ml: 1 }}
                      >
                        Delete
                      </Button>
                    </>
                  )}
                </div>
              </>
            }
          />
        </Stack>

        <Formik
          initialValues={
            {
              [FieldName.INSTRUCTIONAL_TEXT]: clientConfig.instructionalText || "",
              [FieldName.FOOTER_MESSAGE]: clientConfig.projectSubmissionFooterMessage || "",
              [FieldName.PROJECT_SUBMISSION_MODE]: clientConfig.projectSubmissionMode || ProjectSubmissionMode.BOTH,
              [FieldName.ENABLE_PDF_CONVERSION]: !(
                clientConfig.createProjectSettings?.pdfConversion?.isDisabled || false
              ),
              [FieldName.PDF_CONVERSION_TARGET_FORMAT]:
                clientConfig.createProjectSettings?.pdfConversion?.targetFormat ||
                PdfConversionTargetFormat.WORD_DOCUMENT,
              [FieldName.ENABLE_IMAGE_CONVERSION]: !(
                clientConfig.createProjectSettings?.imageConversion?.isDisabled || false
              ),
              [FieldName.DEFAULT_SOURCE_LANGUAGE]: clientConfig.createProjectSettings?.sourceLanguage?.defaultLanguage
                ? {
                    label: clientConfig.createProjectSettings?.sourceLanguage?.defaultLanguage.name,
                    value: clientConfig.createProjectSettings?.sourceLanguage?.defaultLanguage.id
                  }
                : null,
              [FieldName.IS_SOURCE_LANGUAGE_HIDDEN]:
                clientConfig.createProjectSettings?.sourceLanguage?.isHidden || false,
              [FieldName.IS_SOURCE_LANGUAGE_DISABLED]:
                clientConfig.createProjectSettings?.sourceLanguage?.isDisabled || false,
              [FieldName.DISABLE_SOURCE_SHOW_ALL_LANGUAGES]:
                clientConfig.createProjectSettings?.sourceLanguage?.disableShowAllLanguages || false,
              [FieldName.DEFAULT_TARGET_LANGUAGES]: (
                clientConfig.createProjectSettings?.targetLanguages?.defaultLanguages || []
              ).map(l => ({
                label: l.name,
                value: l.id
              })),
              [FieldName.IS_TARGET_LANGUAGE_HIDDEN]:
                clientConfig.createProjectSettings?.targetLanguages?.isHidden || false,
              [FieldName.IS_TARGET_LANGUAGE_DISABLED]:
                clientConfig.createProjectSettings?.targetLanguages?.isDisabled || false,
              [FieldName.DISABLE_TARGET_SHOW_ALL_LANGUAGES]:
                clientConfig.createProjectSettings?.targetLanguages?.disableShowAllLanguages || false,
              [FieldName.SUBMISSION_MODE_CONDITION_FIELD_NAME]:
                (clientConfig.createProjectSettings?.submissionModeConditions || [])[0]?.fieldName || "",
              [FieldName.SUBMISSION_MODE_CONDITION_FIELD_VALUE]:
                (clientConfig.createProjectSettings?.submissionModeConditions || [])[0]?.value || "",
              [FieldName.DISABLE_UNAUTHENTICATED_SUBMISSIONS]:
                clientConfig.createProjectSettings?.disableUnauthenticatedSubmissions || false,
              [FieldName.SKIP_EMAIL_DOMAIN_VALIDATION]:
                clientConfig.createProjectSettings?.skipEmailDomainValidation || false,
              [FieldName.DISABLE_SOURCE_LANGUAGE_DETECTION]:
                clientConfig.createProjectSettings?.disableSourceLanguageDetection || false,
              [FieldName.FORBIDDEN_FILE_TYPES]: FORBIDDEN_FILE_TYPE_OPTIONS.filter(option =>
                (clientConfig.createProjectSettings?.forbiddenFileTypes || []).includes(
                  option.value as ForbiddenFileType
                )
              )
            } as FormValues
          }
          validate={validate}
          onSubmit={onSubmit}
        >
          {({ values }) => (
            <Form noValidate={true} autoComplete="off">
              <ScrollToFieldError />
              <Stack spacing={2}>
                <Grid container spacing={1}>
                  <Grid item={true} xs={12} sm={6}>
                    <CheckboxField
                      name={FieldName.DISABLE_UNAUTHENTICATED_SUBMISSIONS}
                      Label={{ label: "Disable Unauthenticated Submissions" }}
                    />
                  </Grid>
                  {projectCreationToken && (
                    <Grid item={true} xs={12} sm={6}>
                      <CheckboxField
                        name={FieldName.SKIP_EMAIL_DOMAIN_VALIDATION}
                        Label={{ label: "Don't check requestor's email against allowed domains" }}
                      />
                    </Grid>
                  )}
                  <Grid item={true} xs={12} sm={6}>
                    <CheckboxField
                      name={FieldName.DISABLE_SOURCE_LANGUAGE_DETECTION}
                      Label={{ label: "Disable Source Language Detection" }}
                    />
                  </Grid>
                </Grid>
                <LabelValue
                  label="Project Submission Mode"
                  value={
                    <Stack spacing={0}>
                      <SelectOneField
                        name={FieldName.PROJECT_SUBMISSION_MODE}
                        options={[
                          { label: "Send for estimation or translation", value: ProjectSubmissionMode.BOTH },
                          { label: "Send for estimation only", value: ProjectSubmissionMode.SUBMIT_FOR_ESTIMATION },
                          { label: "Send to translation only", value: ProjectSubmissionMode.SUBMIT_FOR_TRANSLATION },
                          {
                            label:
                              "Send for estimation only with exception (Switch to Translation when Field X = Value)",
                            value: ProjectSubmissionMode.SUBMIT_FOR_ESTIMATION_EXCEPT
                          },
                          {
                            label:
                              "Send to translation only with exception (Switch to Estimation when Field X = Value)",
                            value: ProjectSubmissionMode.SUBMIT_FOR_TRANSLATION_EXCEPT
                          }
                        ]}
                        variant="outlined"
                      />
                      {(values[FieldName.PROJECT_SUBMISSION_MODE] ===
                        ProjectSubmissionMode.SUBMIT_FOR_ESTIMATION_EXCEPT ||
                        values[FieldName.PROJECT_SUBMISSION_MODE] ===
                          ProjectSubmissionMode.SUBMIT_FOR_TRANSLATION_EXCEPT) && (
                        <Box pt={2}>
                          <SubmissionModeConditions clientId={clientConfig.id} />
                        </Box>
                      )}
                    </Stack>
                  }
                />
                <Stack spacing={1} border={1} borderColor="divider" p={2} sx={{ mb: 2 }}>
                  <Stack spacing={5} direction="row">
                    <CheckboxField name={FieldName.ENABLE_PDF_CONVERSION} Label={{ label: "Convert PDF Files" }} />
                    <CheckboxField name={FieldName.ENABLE_IMAGE_CONVERSION} Label={{ label: "Convert Image Files" }} />
                  </Stack>
                  {(values[FieldName.ENABLE_PDF_CONVERSION] || values[FieldName.ENABLE_IMAGE_CONVERSION]) && (
                    <Grid item={true} xs={12}>
                      <SelectOneField
                        name={FieldName.PDF_CONVERSION_TARGET_FORMAT}
                        label="Conversion Target Format"
                        options={[
                          { label: "Word Document", value: PdfConversionTargetFormat.WORD_DOCUMENT },
                          { label: "PowerPoint", value: PdfConversionTargetFormat.POWERPOINT },
                          { label: "Excel", value: PdfConversionTargetFormat.EXCEL },
                          { label: "Auto-detect", value: PdfConversionTargetFormat.AUTO }
                        ]}
                        variant="outlined"
                        required={true}
                      />
                    </Grid>
                  )}
                </Stack>
                <Stack spacing={1} border={1} borderColor="divider" p={2} pb={1} sx={{ mb: 2 }}>
                  <Typography sx={theme => ({ color: theme.palette.grey["700"], fontWeight: 600 })}>
                    Source Language Settings
                  </Typography>
                  <Grid container spacing={0}>
                    <Grid item={true} xs={12}>
                      <SelectOneAutocompleteField
                        name={FieldName.DEFAULT_SOURCE_LANGUAGE}
                        label="Default Source Language"
                        options={lltsLanguageOptions}
                        variant="outlined"
                        isLoading={areLltsLanguagesLoading}
                      />
                    </Grid>
                    {values[FieldName.DEFAULT_SOURCE_LANGUAGE] && (
                      <>
                        <Grid item={true} xs={12} sm={6} md={4}>
                          <CheckboxField
                            name={FieldName.IS_SOURCE_LANGUAGE_HIDDEN}
                            Label={{ label: "Hide Source Language" }}
                          />
                        </Grid>
                        <Grid item={true} xs={12} sm={6} md={4}>
                          <CheckboxField
                            name={FieldName.IS_SOURCE_LANGUAGE_DISABLED}
                            Label={{ label: "Disable Source Language" }}
                          />
                        </Grid>
                      </>
                    )}
                    <Grid item={true} xs={12} sm={6} md={4}>
                      <CheckboxField
                        name={FieldName.DISABLE_SOURCE_SHOW_ALL_LANGUAGES}
                        Label={{ label: "Disable Show All Languages" }}
                      />
                    </Grid>
                  </Grid>
                </Stack>
                <Stack spacing={1} border={1} borderColor="divider" p={2} pb={1} sx={{ mb: 2 }}>
                  <Typography sx={theme => ({ color: theme.palette.grey["700"], fontWeight: 600 })}>
                    Target Languages Settings
                  </Typography>
                  <Grid container spacing={0}>
                    <Grid item={true} xs={12}>
                      <SelectManyAutocompleteField
                        name={FieldName.DEFAULT_TARGET_LANGUAGES}
                        label="Default Target Languages"
                        options={lltsLanguageOptions}
                        variant="outlined"
                        isLoading={areLltsLanguagesLoading}
                      />
                    </Grid>
                    {values[FieldName.DEFAULT_TARGET_LANGUAGES].length > 0 && (
                      <>
                        <Grid item={true} xs={12} sm={6} md={4}>
                          <CheckboxField
                            name={FieldName.IS_TARGET_LANGUAGE_HIDDEN}
                            Label={{ label: "Hide Target Languages" }}
                          />
                        </Grid>
                        <Grid item={true} xs={12} sm={6} md={4}>
                          <CheckboxField
                            name={FieldName.IS_TARGET_LANGUAGE_DISABLED}
                            Label={{ label: "Disable Target Languages" }}
                          />
                        </Grid>
                      </>
                    )}
                    <Grid item={true} xs={12} sm={6} md={4}>
                      <CheckboxField
                        name={FieldName.DISABLE_TARGET_SHOW_ALL_LANGUAGES}
                        Label={{ label: "Disable Show All Languages" }}
                      />
                    </Grid>
                  </Grid>
                </Stack>
                <TextInputField
                  label="Instructional Text"
                  helperText="Instructional text is displayed at the top of the portal's project creation form"
                  name={FieldName.INSTRUCTIONAL_TEXT}
                  rows={3}
                  multiline={true}
                  variant="outlined"
                />
                <TextInputField
                  label="Footer message"
                  helperText="Footer message is displayed at the bottom of the portal's project creation form"
                  name={FieldName.FOOTER_MESSAGE}
                  rows={3}
                  multiline={true}
                  variant="outlined"
                />
                <LabelValue
                  label="Forbidden File Types"
                  value={
                    <SelectManyAutocompleteField
                      name={FieldName.FORBIDDEN_FILE_TYPES}
                      label=""
                      options={FORBIDDEN_FILE_TYPE_OPTIONS}
                      variant="outlined"
                    />
                  }
                />
                <Stack direction="row" justifyContent="center" spacing={2} py={2}>
                  <LoadingButton isLoading={isLoading} disabled={isReadOnly}>
                    Save
                  </LoadingButton>
                  <Button onClick={onClose}>Close</Button>
                </Stack>
              </Stack>
            </Form>
          )}
        </Formik>
      </div>

      {mutateError && <SnackbarApiError error={mutateError} />}

      {isBannerUploadDialogOpen && (
        <UploadImageToClientConfigDialog
          clientConfig={clientConfig}
          dataType={ClientDataType.BANNER}
          onSuccess={() => {
            closeBannerUploadDialog();
            onSaveSuccess();
          }}
          onClose={closeBannerUploadDialog}
        />
      )}

      {isDeleteUrlConfirmationDialogOpen && (
        <ConfirmationDialog
          title="Delete Project Creation URL?"
          content={
            <DialogContentText>
              Are you sure you want to delete Project Creation URL? This will make the Project Creation page unavailable
              to users who bookmarked it.
            </DialogContentText>
          }
          onClose={closeDeleteUrlConfirmationDialog}
          onConfirm={onDeleteProjectCreationUrlConfirmation}
          confirmationButtonColor="error"
        />
      )}
    </>
  );
};

export { CreateProjectSettings };
